/** Mirrors the Prisma enums. Keep both sides in sync when adding a member. */

export const Role = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  OPERATOR: 'OPERATOR',
  VIEWER: 'VIEWER',
} as const;
export type Role = (typeof Role)[keyof typeof Role];

/** Ordered from weakest to strongest; used by the RBAC guard for `>=` checks. */
export const ROLE_RANK: Record<Role, number> = {
  VIEWER: 0,
  OPERATOR: 1,
  ADMIN: 2,
  OWNER: 3,
};

/**
 * Session lifecycle. The state machine is linear apart from the reconnect loop:
 *
 *   CREATED -> LAUNCHING -> WAITING_QR -> AUTHENTICATING -> CONNECTED
 *                              ^ |                             |
 *                   QR_EXPIRED-+ |                             v
 *                                +--------- RECONNECTING <- DISCONNECTED
 */
export const SessionStatus = {
  CREATED: 'CREATED',
  LAUNCHING: 'LAUNCHING',
  WAITING_QR: 'WAITING_QR',
  QR_EXPIRED: 'QR_EXPIRED',
  AUTHENTICATING: 'AUTHENTICATING',
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
  RECONNECTING: 'RECONNECTING',
  LOGGED_OUT: 'LOGGED_OUT',
  STOPPED: 'STOPPED',
  ERROR: 'ERROR',
} as const;
export type SessionStatus = (typeof SessionStatus)[keyof typeof SessionStatus];

/** Statuses where a browser is expected to be alive. */
export const LIVE_SESSION_STATUSES: SessionStatus[] = [
  SessionStatus.LAUNCHING,
  SessionStatus.WAITING_QR,
  SessionStatus.QR_EXPIRED,
  SessionStatus.AUTHENTICATING,
  SessionStatus.CONNECTED,
  SessionStatus.RECONNECTING,
];

export const ChatType = {
  PRIVATE: 'PRIVATE',
  GROUP: 'GROUP',
  ROOM: 'ROOM',
  OFFICIAL: 'OFFICIAL',
  UNKNOWN: 'UNKNOWN',
} as const;
export type ChatType = (typeof ChatType)[keyof typeof ChatType];

export const MessageType = {
  TEXT: 'TEXT',
  STICKER: 'STICKER',
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  AUDIO: 'AUDIO',
  FILE: 'FILE',
  LOCATION: 'LOCATION',
  CONTACT: 'CONTACT',
  CALL: 'CALL',
  SYSTEM: 'SYSTEM',
  RECALLED: 'RECALLED',
  /**
   * A LINE Flex Message - a JSON-defined layout LINE renders in its own iframe,
   * used by official accounts for receipts, alerts and promotions. The bubble
   * carries no readable text node, so what we store is the alt text its sender
   * had to supply: the same line LINE itself shows in the chat list and in a
   * push notification.
   */
  FLEX: 'FLEX',
  UNKNOWN: 'UNKNOWN',
} as const;
export type MessageType = (typeof MessageType)[keyof typeof MessageType];

export const Direction = {
  INBOUND: 'INBOUND',
  OUTBOUND: 'OUTBOUND',
} as const;
export type Direction = (typeof Direction)[keyof typeof Direction];

export const DeliveryStatus = {
  PENDING: 'PENDING',
  DELIVERING: 'DELIVERING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  DEAD: 'DEAD',
} as const;
export type DeliveryStatus = (typeof DeliveryStatus)[keyof typeof DeliveryStatus];

export const AiProvider = {
  OPENAI: 'OPENAI',
  ANTHROPIC: 'ANTHROPIC',
  GEMINI: 'GEMINI',
  OPENROUTER: 'OPENROUTER',
} as const;
export type AiProvider = (typeof AiProvider)[keyof typeof AiProvider];

export const AiTask = {
  SUMMARIZE: 'SUMMARIZE',
  INTENT: 'INTENT',
  SENTIMENT: 'SENTIMENT',
  KEYWORDS: 'KEYWORDS',
  AUTO_REPLY: 'AUTO_REPLY',
  SPAM: 'SPAM',
  CATEGORY: 'CATEGORY',
} as const;
export type AiTask = (typeof AiTask)[keyof typeof AiTask];

export const NotificationChannel = {
  BROWSER: 'BROWSER',
  EMAIL: 'EMAIL',
  WEBHOOK: 'WEBHOOK',
  LINE_MESSAGING_API: 'LINE_MESSAGING_API',
} as const;
export type NotificationChannel =
  (typeof NotificationChannel)[keyof typeof NotificationChannel];

export const KeywordMatchMode = {
  CONTAINS: 'CONTAINS',
  EXACT: 'EXACT',
  REGEX: 'REGEX',
} as const;
export type KeywordMatchMode = (typeof KeywordMatchMode)[keyof typeof KeywordMatchMode];

export const LogLevel = {
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR',
} as const;
export type LogLevel = (typeof LogLevel)[keyof typeof LogLevel];
